// SmartInventory Design System — MUI v6 theme mapping
// Direction "Plum" 2b, refined to the light/elegant density signed off in review.
// Every value here is the value used in the approved screens — no page should
// re-declare type sizes, radii, borders or heights in `sx`.
//
// Usage:
//   import { lightTheme, darkTheme } from './themes/smartinventoryTheme';
//   <ThemeProvider theme={mode === 'dark' ? darkTheme : lightTheme}>

import { createTheme } from '@mui/material/styles';

/* ------------------------------------------------------------------ tokens */

export const tokens = {
  light: {
    canvas: '#FBF9FC',        // page background
    surface: '#FFFFFF',       // cards, tables, drawers
    surfaceSunken: '#FBF9FC', // table headers, totals panels, inset blocks
    border: '#EDE7F1',        // all 1px component borders
    borderSoft: '#F5F1F8',    // row dividers inside a surface
    borderStrong: '#DCD3E3',  // hover borders, dashed placeholders
    ink: '#2A1E33',           // primary text
    inkSoft: '#55495F',       // body copy, field labels
    muted: '#8B7E96',         // secondary text, subtitles
    faint: '#B4A9BE',         // helper text, column headers, placeholders
    accent: '#7C3AED',
    accentHover: '#6425D0',
    accentTint: '#F4F0FE',    // active nav, selected row
    accentTintStrong: '#EDE4FD',
    accentTintSoft: '#FAF6FF', // Help system (step 9) — tour launcher card background
    accentBorder: '#CDB1F9',
    accentInk: '#5B21B6',     // accent text on tint
    neutralTint: '#F1EBF6',  // Help system (step 9) — "other page" search result tag
    success: '#188A5E', successTint: '#E7F5EE', successInk: '#116B48',
    warning: '#C77414', warningTint: '#FFF6E5', warningInk: '#8A6410', warningBorder: '#F0DEB4',
    error: '#D33F49', errorTint: '#FBEBEC', errorInk: '#A32B34', errorBorder: '#F2C6C9',
    info: '#2563EB', infoTint: '#F4F0FE', infoBorder: '#DFD2FA',
    documentSurface: '#FFFFFF',
  },
  dark: {
    canvas: '#171221', surface: '#201936', surfaceSunken: '#1B1530',
    border: '#342A4A', borderSoft: '#2B2240', borderStrong: '#453963',
    ink: '#F1ECFA', inkSoft: '#C9C0D8', muted: '#A79DB8',
    faint: '#6F6584',         // NON-TEXT ONLY — borders, dots, disabled icons (3.1:1 on surface)
    accent: '#A78BFA', accentHover: '#B9A3FC',
    accentTint: 'rgba(167,139,250,0.14)', accentTintStrong: 'rgba(167,139,250,0.22)',
    accentTintSoft: 'rgba(167,139,250,0.08)', // Help system (step 9)
    accentBorder: '#5B4791', accentInk: '#C4A4FF',
    neutralTint: 'rgba(255,255,255,0.05)', // Help system (step 9)
    success: '#3ECF8E', successTint: 'rgba(62,207,142,0.14)', successInk: '#6FE0AC',
    warning: '#E8A13C', warningTint: 'rgba(232,161,60,0.14)', warningInk: '#F0BE74', warningBorder: '#5A4520',
    error: '#F2707A', errorTint: 'rgba(242,112,122,0.14)', errorInk: '#F79BA2', errorBorder: '#5E2C31',
    info: '#6EA8FE', infoTint: 'rgba(110,168,254,0.14)', infoBorder: '#33436B',
    documentSurface: '#FFFFFF',
  },
};

/* Category data colours. Two full sets — the light hues go muddy on a dark
   surface, so dark is re-picked at the same hue, not alpha-shifted.
   Read with categoryColors[mode][key]; admins can still pick any hex.
   Keys are the seeded categories; `none` covers uncategorised. */
export const categoryColors = {
  light: {
    packaging: { dot: '#D97706', tint: '#FDF1DE', ink: '#8A4B08' },
    safety:    { dot: '#16A34A', tint: '#E5F5EB', ink: '#14532D' },
    office:    { dot: '#2563EB', tint: '#E7EFFD', ink: '#1E40AF' },
    equipment: { dot: '#DB2777', tint: '#FCE7F3', ink: '#9D174D' },
    foodBev:   { dot: '#8B5E3C', tint: '#F1EAE4', ink: '#5C4030' },
    cleaning:  { dot: '#0891B2', tint: '#E1F3F8', ink: '#155E75' },
    none:      { dot: '#DCD3E3', tint: '#F5F1F8', ink: '#8B7E96' },
  },
  dark: {
    packaging: { dot: '#E8A13C', tint: 'rgba(232,161,60,0.16)',  ink: '#F0BE74' },
    safety:    { dot: '#3ECF8E', tint: 'rgba(62,207,142,0.16)',  ink: '#7FE3B4' },
    office:    { dot: '#6EA8FE', tint: 'rgba(110,168,254,0.16)', ink: '#A6C8FF' },
    equipment: { dot: '#F472B6', tint: 'rgba(244,114,182,0.16)', ink: '#F9A8D4' },
    foodBev:   { dot: '#C08A63', tint: 'rgba(192,138,99,0.16)',  ink: '#D9B394' },
    cleaning:  { dot: '#4FC3DE', tint: 'rgba(79,195,222,0.16)',  ink: '#93DBEC' },
    none:      { dot: '#453963', tint: 'rgba(255,255,255,0.06)', ink: '#A79DB8' },
  },
};

/* Contrast rule: `faint` is a DECORATIVE tone (hairlines, dots, disabled icons).
   Any text 18px or smaller — helper text, column headers, placeholders, axis
   labels — uses `muted` as its lightest permitted colour. Verified AA:
   light muted #8B7E96 on #FFF = 4.5:1 · dark muted #A79DB8 on #201936 = 6.5:1. */

/* Order / invoice status → chip colours. Pages read this, never raw hex. */
export const statusColors = (t) => ({
  pending:   { bg: t.warningTint, ink: t.warningInk },
  accepted:  { bg: t.accentTint,  ink: t.accentInk },
  paid:      { bg: t.successTint, ink: t.successInk },
  rejected:  { bg: t.errorTint,   ink: t.errorInk },
  draft:     { bg: t.surfaceSunken, ink: t.muted },
  issued:    { bg: t.accentTint,  ink: t.accentInk },
  overdue:   { bg: t.errorTint,   ink: t.errorInk },
  void:      { bg: t.surfaceSunken, ink: t.muted },
  processing: { bg: t.warningTint, ink: t.warningInk },
  // Advertisements (step 5) — reuses the same semantic tones.
  active:    { bg: t.successTint, ink: t.successInk },
  scheduled: { bg: t.accentTint,  ink: t.accentInk },
  expired:   { bg: t.errorTint,   ink: t.errorInk },
  disabled:  { bg: t.surfaceSunken, ink: t.muted },
  // StockLevel (step 3) — same success/warning/error tones, no new hex.
  inStock:    { bg: t.successTint, ink: t.successInk },
  lowStock:   { bg: t.warningTint, ink: t.warningInk },
  outOfStock: { bg: t.errorTint,   ink: t.errorInk },
  // Invoice email delivery status (Resend webhook) — same semantic tones, no new hex.
  emailSent:      { bg: t.accentTint,   ink: t.accentInk },
  emailDelivered: { bg: t.successTint,  ink: t.successInk },
  emailOpened:    { bg: t.successTint,  ink: t.successInk },
  emailClicked:   { bg: t.successTint,  ink: t.successInk },
  emailBounced:   { bg: t.errorTint,    ink: t.errorInk },
  emailFailed:    { bg: t.errorTint,    ink: t.errorInk },
});

export const radius = { xs: 6, sm: 8, md: 12, lg: 14, xl: 20, pill: 999 };

/* In-view Help system (step 9): the `?` mark popover and the tour overlay
   ALWAYS use this fixed dark chrome, regardless of the app's light/dark mode
   — a deliberate exception (design_handoff_help_system/README.md) so
   contextual help reads identically in both themes. Not a per-mode token
   like the rest of this file on purpose. */
export const helpDark = {
  bg: '#2A1E33',
  text: '#F1ECFA',
  body: '#C9C0D8',
  link: '#C4A4FF',
  overlay: 'rgba(42,30,51,0.55)',
};

/* Two shadows everywhere in the product, plus two more added for the
   in-view Help system (step 9) whose design specifies its own exact values
   (design_handoff_help_system/README.md) — the persistent help Drawer sits
   flush against page content (no surrounding chrome an overlay shadow would
   read against) and the dark `?` popover needs to lift off content in both
   themes, so neither reuses `menu`/`overlay`. */
export const elevation = {
  menu: '0 6px 18px rgba(42,30,51,0.07)',   // selects, popovers, dropdowns
  overlay: '0 24px 60px rgba(42,30,51,0.24)', // dialogs, drawers, bottom sheets
  helpPanel: '-12px 0 32px rgba(42,30,51,0.06)', // persistent help Drawer, cast toward the page
  helpPopover: '0 12px 32px rgba(42,30,51,0.28)', // HelpMark's dark popover (step 9, layer 2)
};

export const layout = {
  sidebarWidth: 248,
  railWidth: 64,
  mobileDrawerWidth: 184,
  pagePaddingDesktop: 32,
  pagePaddingTablet: 20,
  pagePaddingMobile: 14,
  drawerWidth: 560,        // entity create/edit
  drawerWidthWide: 620,    // wizards, product picker
  contentMax: 1440,
  // AppShell (step 2) — geometry the design specifies for the sidebar/rail/
  // mobile chrome that has no other home in the theme.
  navItemHeight: 34,
  navIconSize: 17,
  railButtonSize: 36,
  mobileTapTarget: 44,     // minimum tap target, mobile drawer + bottom bar
  bottomBarHeight: 58,
  mobileTopBarHeight: 56,
  // Shared components (step 3)
  rowThumbSize: 46,        // DataTableCard / RowThumb — fixed so images never inflate row height
  stateIconSize: 56,       // Empty/Error/PermissionState icon roundel
  // Help system (step 9)
  helpPanelWidth: 380,
  helpButtonSize: 38,
};

/* ------------------------------------------------------------------- theme */

const buildTheme = (mode) => {
  const t = tokens[mode];
  const dark = mode === 'dark';
  const onAccent = dark ? '#1E1433' : '#FFFFFF';

  return createTheme({
    breakpoints: {
      // sm/md are the two that matter: md = rail↔sidebar, sm = mobile↔rail
      values: { xs: 0, sm: 900, md: 1200, lg: 1440, xl: 1680 },
    },
    palette: {
      mode,
      primary: { main: t.accent, dark: t.accentHover, light: t.accentTint, contrastText: onAccent },
      // `light` on each maps to that token's own *Tint value (not MUI's
      // auto-lightened default) so `color="success"` etc. resolves to the
      // same tint the design system specifies for badges/icon roundels.
      success: { main: t.success, light: t.successTint, dark: t.successInk },
      warning: { main: t.warning, light: t.warningTint, dark: t.warningInk },
      error: { main: t.error, light: t.errorTint, dark: t.errorInk },
      info: { main: t.info, light: t.infoTint },
      background: { default: t.canvas, paper: t.surface },
      documentSurface: t.documentSurface,
      divider: t.border,
      text: { primary: t.ink, secondary: t.muted, disabled: t.muted },
      action: { hover: t.canvas, selected: t.accentTint, disabledBackground: t.borderSoft },
    },
    shape: { borderRadius: radius.md },
    spacing: 4,
    shadows: ['none', elevation.menu, elevation.menu, elevation.overlay,
      ...Array(21).fill(elevation.overlay)],

    typography: {
      fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
      // Page titles are the only large type in the product.
      h4: { fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 },
      h5: { fontSize: 17, fontWeight: 600, lineHeight: 1.3 },   // section titles
      h6: { fontSize: 15.5, fontWeight: 600, lineHeight: 1.35 }, // card + drawer titles
      subtitle1: { fontSize: 13.5, fontWeight: 600 },   // list/row titles
      subtitle2: { fontSize: 12, fontWeight: 600 },     // field labels
      body1: { fontSize: 13.5, fontWeight: 400, lineHeight: 1.55 },
      body2: { fontSize: 12.5, fontWeight: 400, lineHeight: 1.55 },
      caption: { fontSize: 11.5, fontWeight: 500, lineHeight: 1.45 }, // helper text
      overline: { fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.4 },
      button: { textTransform: 'none', fontWeight: 600, fontSize: 13 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.canvas, color: t.ink, WebkitFontSmoothing: 'antialiased' },
          a: { color: t.accent, textDecoration: 'none' },
          'a:hover': { color: t.accentHover, textDecoration: 'underline' },
          '::selection': { background: t.accentTintStrong },
        },
      },

      /* --- actions --- */
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: { borderRadius: radius.pill, height: 38, paddingInline: 18, minWidth: 0, whiteSpace: 'nowrap' },
          sizeSmall: { height: 34, paddingInline: 14, fontSize: 12.5 },
          sizeLarge: { height: 44, paddingInline: 22, fontSize: 13.5 },
          contained: { '&.Mui-disabled': { backgroundColor: t.accentTintStrong, color: t.muted } },
          outlined: {
            borderColor: t.border, color: t.ink,
            '&:hover': { borderColor: t.borderStrong, backgroundColor: dark ? 'rgba(255,255,255,0.04)' : '#F1EBF6' },
          },
          text: { paddingInline: 12, color: t.inkSoft, '&:hover': { backgroundColor: 'transparent', color: t.ink } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: radius.sm, color: t.muted, '&:hover': { backgroundColor: t.canvas, color: t.ink } },
          sizeSmall: { padding: 5 },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: { root: { backgroundColor: t.surfaceSunken, borderRadius: radius.pill, padding: 3 } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 'none', borderRadius: radius.pill, height: 30, paddingInline: 14,
            textTransform: 'none', fontSize: 12.5, fontWeight: 600, color: t.muted,
            '&.Mui-selected': { backgroundColor: t.surface, color: t.accent, boxShadow: elevation.menu },
          },
        },
      },

      /* --- surfaces --- */
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: radius.xl },
          outlined: { borderColor: t.border },
        },
      },
      MuiCard: { defaultProps: { variant: 'outlined' }, styleOverrides: { root: { borderRadius: radius.xl } } },
      MuiCardContent: { styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.borderSoft } } },

      /* --- inputs --- */
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md, backgroundColor: t.surface, fontSize: 13.5,
            '& fieldset': { borderColor: t.border },
            '&:hover fieldset': { borderColor: t.borderStrong },
            '&.Mui-focused fieldset': { borderColor: t.accent, borderWidth: 1.5 },
            '&.Mui-disabled': { backgroundColor: t.surfaceSunken },
          },
          input: { height: 40, paddingTop: 0, paddingBottom: 0, paddingInline: 13, boxSizing: 'border-box' },
          multiline: { padding: 0 },
          inputMultiline: { height: 'auto', padding: '10px 13px' },
        },
      },
      MuiInputLabel: {
        // Labels sit ABOVE the field in this system, not floating inside it.
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            position: 'static', transform: 'none', fontSize: 12, fontWeight: 600,
            color: t.inkSoft, marginBottom: 5, '&.Mui-focused': { color: t.inkSoft },
          },
        },
      },
      MuiFormHelperText: { styleOverrides: { root: { fontSize: 11.5, marginInline: 0, marginTop: 5, color: t.muted } } },
      MuiSelect: {
        styleOverrides: {
          icon: { color: t.muted },
          // MUI resets the select's height to auto, which with the zero vertical
          // padding above collapses it to one text line; match the 40px inputs.
          outlined: { display: 'flex', alignItems: 'center', minHeight: 40, boxSizing: 'border-box' },
        },
      },
      MuiMenu: { styleOverrides: { paper: { borderRadius: radius.md, border: `1px solid ${t.border}`, boxShadow: elevation.menu } } },
      MuiMenuItem: { styleOverrides: { root: { fontSize: 13, minHeight: 34, borderRadius: radius.xs, marginInline: 5 } } },
      MuiCheckbox: { defaultProps: { size: 'small' }, styleOverrides: { root: { color: t.borderStrong, padding: 6 } } },
      MuiRadio: { defaultProps: { size: 'small' }, styleOverrides: { root: { color: t.borderStrong, padding: 6 } } },
      MuiSwitch: { defaultProps: { size: 'small' } },

      /* --- navigation --- */
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40, borderBottom: `1.5px solid ${t.border}` },
          indicator: { height: 2.5, borderRadius: '2px 2px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none', fontWeight: 500, fontSize: 13.5, minHeight: 40,
            paddingInline: 14, color: t.muted, whiteSpace: 'nowrap',
            '&.Mui-selected': { fontWeight: 600, color: t.accent },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm, minHeight: 34, paddingInline: 10, gap: 10,
            '&.Mui-selected': {
              backgroundColor: t.accentTint,
              '& .MuiListItemText-primary': { fontWeight: 600, color: t.accent },
              '& .MuiListItemIcon-root': { color: t.accent },
              '&:hover': { backgroundColor: t.accentTint },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 0, color: t.muted, '& svg': { fontSize: 18 } } } },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: 13, fontWeight: 500, color: t.inkSoft },
          secondary: { fontSize: 11.5, color: t.muted },
        },
      },
      MuiBreadcrumbs: { styleOverrides: { root: { fontSize: 12.5, color: t.muted }, separator: { color: t.faint } } },

      /* --- chips & badges --- */
      MuiChip: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { borderRadius: radius.pill, fontWeight: 600, fontSize: 12, height: 24 },
          sizeSmall: { fontSize: 11.5, height: 22, paddingInline: 2 },
          outlined: { borderColor: t.border, color: t.inkSoft },
          deleteIcon: { fontSize: 15 },
        },
      },
      MuiAvatar: { styleOverrides: { root: { borderRadius: radius.sm, fontSize: 11, fontWeight: 600, width: 28, height: 28, backgroundColor: t.accentTintStrong, color: t.accent } } },

      /* --- overlays --- */
      MuiDialog: {
        styleOverrides: {
          // Below `sm` (900px) every dialog becomes a bottom sheet: docked to
          // the bottom edge, full width, only the top corners rounded.
          container: ({ theme: th }) => ({
            [th.breakpoints.down('sm')]: { alignItems: 'flex-end' },
          }),
          paper: ({ theme: th }) => ({
            borderRadius: radius.xl, boxShadow: elevation.overlay, border: `1px solid ${t.border}`,
            '&&': {
              [th.breakpoints.down('sm')]: {
                width: '100%', maxWidth: '100%', margin: 0, maxHeight: '85vh',
                borderRadius: `${radius.xl}px ${radius.xl}px 0 0`,
              },
            },
          }),
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: 15, fontWeight: 600, padding: '18px 22px 0' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '12px 22px 20px', fontSize: 12.5 } } },
      MuiDialogContentText: { styleOverrides: { root: { fontSize: 12.5, color: t.inkSoft, lineHeight: 1.55 } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '14px 22px', borderTop: `1px solid ${t.border}`, gap: 10 } } },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRadius: 0, borderColor: t.border, backgroundImage: 'none' },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.ink, color: dark ? '#171221' : '#FFFFFF', fontSize: 11.5, fontWeight: 600, borderRadius: radius.sm, padding: '6px 9px' },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: { root: { backgroundColor: t.ink, color: dark ? '#171221' : '#FFFFFF', borderRadius: radius.md, fontSize: 12.5, fontWeight: 600 } },
      },
      MuiAlert: {
        defaultProps: { variant: 'outlined', icon: undefined },
        styleOverrides: {
          root: { borderRadius: radius.md, fontSize: 12.5, alignItems: 'flex-start', paddingBlock: 9 },
          standardInfo: { backgroundColor: t.infoTint, borderColor: t.infoBorder, color: t.inkSoft },
          outlinedInfo: { backgroundColor: t.infoTint, borderColor: t.infoBorder, color: t.inkSoft },
          outlinedError: { backgroundColor: t.errorTint, borderColor: t.errorBorder, color: t.inkSoft },
          outlinedWarning: { backgroundColor: t.warningTint, borderColor: t.warningBorder, color: t.inkSoft },
          outlinedSuccess: { backgroundColor: t.successTint, borderColor: t.success, color: t.inkSoft },
          /* center the 17px icon on the message's first line box: the text
             column starts at 8px padding with a ~18px line, so the line's
             center sits ~17px down — 8.5px padding puts the icon center
             there too, instead of floating above the text */
          icon: { fontSize: 17, marginRight: 9, padding: '8.5px 0' },
        },
      },

      /* --- feedback --- */
      MuiSkeleton: {
        defaultProps: { animation: 'wave', variant: 'rounded' },
        styleOverrides: { root: { backgroundColor: t.borderSoft, borderRadius: radius.xs } },
      },
      MuiLinearProgress: { styleOverrides: { root: { height: 5, borderRadius: radius.pill, backgroundColor: t.borderSoft } } },
      MuiCircularProgress: { defaultProps: { size: 18, thickness: 4.5 } },

      /* --- data grid --- */
      MuiDataGrid: {
        defaultProps: { rowHeight: 52, columnHeaderHeight: 36, disableColumnMenu: true, disableRowSelectionOnClick: true },
        styleOverrides: {
          root: {
            border: `1px solid ${t.border}`, borderRadius: radius.xl, backgroundColor: t.surface,
            fontSize: 13, '--DataGrid-rowBorderColor': t.borderSoft,
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: `1.5px solid ${t.accent}`, outlineOffset: -2 },
          },
          columnHeaders: { backgroundColor: t.surfaceSunken, borderBottom: `1px solid ${t.border}` },
          columnHeaderTitle: { color: t.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
          row: {
            '&:hover': { backgroundColor: t.canvas },
            '&.Mui-selected': { backgroundColor: t.accentTint, '&:hover': { backgroundColor: t.accentTint } },
          },
          cell: { borderColor: t.borderSoft, paddingInline: 14 },
          footerContainer: { borderTop: `1px solid ${t.border}`, minHeight: 46 },
          overlay: { backgroundColor: t.surface },
          columnSeparator: { display: 'none' },
        },
      },
    },
  });
};

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');
